import React, { useEffect, useState } from 'react';
import { MessageSquareWarning, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SmsVerifyBanner — in-app alert when the ClickSend "Own Number" sender is
 * expired or inside the 30-day renewal window. Tapping "Verify now" opens a
 * modal that texts a code to the number and verifies it without leaving the
 * app (same UX as Squeegee's number re-verification).
 */
const SmsVerifyBanner = ({ user }) => {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [step, setStep] = useState('idle'); // idle | code
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const isOwner = user && user.role !== 'Worker';
  const token = () => localStorage.getItem('streakfree_token');

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/clicksend/status`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) return;
      setStatus(await res.json());
    } catch {
      /* network blip — no banner */
    }
  };

  useEffect(() => {
    if (!isOwner) return;
    if (sessionStorage.getItem('smsVerifyBannerDismissed') === '1') {
      setDismissed(true);
      return;
    }
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner]);

  const needsVerify =
    status &&
    status.sender_type === 'own_number' &&
    typeof status.days_until_expiry === 'number' &&
    status.days_until_expiry <= 30;

  if (!isOwner || dismissed || !needsVerify) return null;

  const expired = status.days_until_expiry < 0;

  const sendCode = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/clicksend/sender/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ phone_number: status.sender_id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not send the code');
      toast.success(`Code texted to ${data.phone_number || status.sender_id}`);
      setStep('code');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/clicksend/sender/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ otp_code: code.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Verification failed');
      toast.success('Number verified — SMS sending is good for another year');
      setModalOpen(false);
      setStep('idle');
      setCode('');
      fetchStatus();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const tone = expired
    ? { bg: 'bg-red-50 border-red-200', text: 'text-red-900', icon: 'text-red-600', btn: 'bg-red-500 hover:bg-red-600', xHover: 'hover:bg-red-100', x: 'text-red-700' }
    : { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-900', icon: 'text-amber-600', btn: 'bg-amber-500 hover:bg-amber-600', xHover: 'hover:bg-amber-100', x: 'text-amber-700' };

  const label = expired
    ? `SMS number ${status.sender_id} verification expired`
    : `SMS number needs re-verifying in ${status.days_until_expiry} day${status.days_until_expiry === 1 ? '' : 's'}`;

  return (
    <div data-testid="sms-verify-banner">
      <div className="safe-area-top bg-zinc-900" aria-hidden="true" />
      <div className={`${tone.bg} border-b px-4 py-2.5 flex items-center justify-between gap-3`}>
        <div className="flex items-center gap-2 min-w-0">
          <MessageSquareWarning size={18} className={`${tone.icon} flex-shrink-0`} />
          <p className={`text-sm ${tone.text} truncate`}>
            <span className="font-semibold">{label}</span>
            <span className="hidden sm:inline"> — texts stop sending once it lapses.</span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setModalOpen(true)}
            className={`px-3 py-1.5 ${tone.btn} text-white text-xs font-medium rounded-lg transition-colors`}
            data-testid="sms-verify-now-btn"
          >
            Verify now
          </button>
          <button
            onClick={() => { sessionStorage.setItem('smsVerifyBannerDismissed', '1'); setDismissed(true); }}
            className={`p-1.5 ${tone.xHover} rounded-lg transition-colors`}
            aria-label="Dismiss"
            data-testid="sms-verify-dismiss-btn"
          >
            <X size={16} className={tone.x} />
          </button>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" data-testid="sms-verify-modal">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <h3 className="text-lg font-bold text-zinc-900 mb-1">Re-verify your SMS number</h3>
            <p className="text-sm text-zinc-500 mb-4">
              ClickSend requires your sender number <span className="font-semibold">{status.sender_id}</span> to be re-verified periodically.
            </p>
            {step === 'idle' ? (
              <button
                onClick={sendCode}
                disabled={busy}
                className="w-full py-3 rounded-xl bg-orange-500 text-white font-semibold text-sm disabled:opacity-60"
                data-testid="sms-verify-send-code-btn"
              >
                {busy ? 'Sending…' : `Text a code to ${status.sender_id}`}
              </button>
            ) : (
              <>
                <input
                  type="text"
                  inputMode="numeric"
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Enter the 6-digit code"
                  className="w-full border border-zinc-300 rounded-xl px-4 py-3 text-center text-lg tracking-widest mb-3"
                  data-testid="sms-verify-code-input"
                />
                <button
                  onClick={verifyCode}
                  disabled={busy || code.trim().length < 4}
                  className="w-full py-3 rounded-xl bg-orange-500 text-white font-semibold text-sm disabled:opacity-60"
                  data-testid="sms-verify-confirm-btn"
                >
                  {busy ? 'Verifying…' : 'Verify'}
                </button>
                <button onClick={sendCode} disabled={busy} className="w-full mt-2 py-2 text-xs text-zinc-500" data-testid="sms-verify-resend-btn">
                  Resend code
                </button>
              </>
            )}
            <button
              onClick={() => { setModalOpen(false); setStep('idle'); setCode(''); }}
              className="w-full mt-2 py-2 text-sm text-zinc-500"
              data-testid="sms-verify-cancel-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmsVerifyBanner;
